import { useMutation, useQueryClient } from '@tanstack/react-query';

import { weatherApi } from 'api/weather';

import { weatherReportListQueryKeys } from './useWeatherReportListQuery';

/**
 * Requests a new weather report; on success invalidates weather report queries so the list refetches.
 *
 * @param {Omit<import('@tanstack/react-query').UseMutationOptions<Object, Error, {deliveryType: *, orderSource: Array, reportType: *}>, 'mutationFn'>} [mutationOptions={]}
 * @returns {import('@tanstack/react-query').UseMutationResult<Object, Error, {deliveryType: *, orderSource: Array, reportType: *}>}
 */
export function useCreateWeatherReportMutation(mutationOptions = {}) {
    const queryClient = useQueryClient();
    const { onSuccess, ...rest } = mutationOptions;

    return useMutation({
        mutationFn: async ({ deliveryType, orderSource, reportType }) => {
            const response = await weatherApi.createWeatherReport(orderSource, deliveryType, reportType);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to request new weather report');
            }
            return response.data;
        },
        onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: weatherReportListQueryKeys.all });
            return onSuccess?.(...args);
        },
        ...rest,
    });
}
